import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  AlertTriangle,
  Check,
  CheckCircle2,
  Clipboard,
  Clock,
  MessageSquare,
  MoreHorizontal,
  PauseCircle,
  PlayCircle,
  Rocket,
  Send,
  ShieldCheck,
  Sparkles,
  ThumbsDown,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";

type Verdict = "accurate" | "incorrect" | "outdated" | "never_use" | "promote_to_verified";

interface SalesIntelligenceProps {
  businessId: string;
  contactId: string;
}

interface InvokeInput extends SalesIntelligenceProps {
  action: string;
  [key: string]: unknown;
}

async function invokeSalesIntelligence(input: InvokeInput) {
  const { data, error } = await supabase.functions.invoke("customer-sales-intelligence", {
    body: {
      action: input.action,
      business_id: input.businessId,
      contact_id: input.contactId,
      ...Object.fromEntries(
        Object.entries(input).filter(([key]) => !["action", "businessId", "contactId"].includes(key)),
      ),
    },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
}

export function ClaimFeedbackMenu({
  businessId,
  contactId,
  claimKey,
  claimText,
}: SalesIntelligenceProps & { claimKey: string; claimText: string }) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async ({ verdict, correction }: { verdict: Verdict; correction?: string }) =>
      invokeSalesIntelligence({
        action: "submit_feedback",
        businessId,
        contactId,
        claim_key: claimKey,
        claim_text: claimText,
        verdict,
        correction,
      }),
    onSuccess: async (data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["sales_intelligence_state", businessId, contactId] }),
        queryClient.invalidateQueries({ queryKey: ["sales_recommendation", businessId, contactId] }),
        queryClient.invalidateQueries({ queryKey: ["contact_projection", contactId] }),
      ]);
      const labels: Record<Verdict, string> = {
        accurate: "Marked accurate",
        incorrect: "Marked incorrect",
        outdated: "Marked outdated",
        never_use: "This claim will never be used",
        promote_to_verified: "Saved as a verified fact",
      };
      toast.success(labels[variables.verdict], {
        description: data?.refresh_queued ? "A governed profile refresh has been queued." : undefined,
      });
    },
    onError: (error: Error) => toast.error(error.message || "Could not save feedback"),
  });

  const submit = (verdict: Verdict) => {
    let correction: string | undefined;
    if (verdict === "incorrect") {
      const value = window.prompt("What should this claim say instead? Leave blank to reject without a correction.");
      if (value === null) return;
      correction = value.trim() || undefined;
    }
    if (verdict === "promote_to_verified") {
      const value = window.prompt("Confirm the verified fact:", claimText);
      if (value === null || !value.trim()) return;
      correction = value.trim();
    }
    mutation.mutate({ verdict, correction });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={mutation.isPending}
          aria-label="Review this inferred claim"
          className="h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground"
        >
          <MoreHorizontal className="h-3.5 w-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuItem onClick={() => submit("accurate")}>
          <Check className="mr-2 h-4 w-4" /> Accurate
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => submit("incorrect")}>
          <X className="mr-2 h-4 w-4" /> Incorrect
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => submit("outdated")}>
          <Clock className="mr-2 h-4 w-4" /> Outdated
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => submit("never_use")} className="text-destructive">
          <Trash2 className="mr-2 h-4 w-4" /> Never use
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => submit("promote_to_verified")}>
          <ShieldCheck className="mr-2 h-4 w-4" /> Save as verified fact
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function InferredClaim({
  businessId,
  contactId,
  claimKey,
  claimText,
  className = "text-xs leading-relaxed",
}: SalesIntelligenceProps & {
  claimKey: string;
  claimText: string;
  className?: string;
}) {
  return (
    <div className="flex items-start gap-1">
      <p className={className}>{claimText}</p>
      <ClaimFeedbackMenu
        businessId={businessId}
        contactId={contactId}
        claimKey={claimKey}
        claimText={claimText}
      />
    </div>
  );
}

export default function CustomerSalesIntelligenceCard({ businessId, contactId }: SalesIntelligenceProps) {
  const queryClient = useQueryClient();
  const [draftText, setDraftText] = useState("");

  const stateQuery = useQuery({
    queryKey: ["sales_intelligence_state", businessId, contactId],
    queryFn: () => invokeSalesIntelligence({ action: "get_state", businessId, contactId }),
    enabled: !!businessId && !!contactId,
  });
  const recommendationQuery = useQuery({
    queryKey: ["sales_recommendation", businessId, contactId],
    queryFn: () => invokeSalesIntelligence({ action: "get_recommendation", businessId, contactId }),
    enabled: !!businessId && !!contactId,
    staleTime: 60_000,
  });
  const eligibility = recommendationQuery.data?.eligibility ?? null;
  const latestRecommendation = recommendationQuery.data?.recommendation ?? stateQuery.data?.recommendation ?? null;
  const recommendation = eligibility?.eligible === false ? null : latestRecommendation;

  useEffect(() => {
    if (!recommendationQuery.data?.recommendation?.id) return;
    void queryClient.invalidateQueries({
      queryKey: ["sales_intelligence_state", businessId, contactId],
    });
  }, [
    businessId,
    contactId,
    queryClient,
    recommendationQuery.data?.recommendation?.id,
  ]);

  useEffect(() => {
    setDraftText(recommendation?.edited_text ?? recommendation?.draft_text ?? "");
  }, [recommendation?.id, recommendation?.edited_text, recommendation?.draft_text]);

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["sales_intelligence_state", businessId, contactId] }),
      queryClient.invalidateQueries({ queryKey: ["sales_recommendation", businessId, contactId] }),
    ]);
  };
  const actionMutation = useMutation({
    mutationFn: (input: { action: string; [key: string]: unknown }) =>
      invokeSalesIntelligence({ businessId, contactId, ...input }),
    onSuccess: async () => refresh(),
    onError: (error: Error) => toast.error(error.message || "Could not update recommendation"),
  });

  const createDraft = () => actionMutation.mutate({
    action: "create_draft",
    recommendation_id: recommendation.id,
  }, {
    onSuccess: (data) => toast.success(
      data.generation === "ai" ? "Grounded draft created" : "Safe template draft created",
    ),
  });
  const saveDraft = () => actionMutation.mutate({
    action: "update_draft",
    recommendation_id: recommendation.id,
    draft_text: draftText,
  }, { onSuccess: () => toast.success("Draft edits saved") });
  const recordAction = (recommendationAction: string) => actionMutation.mutate({
    action: "record_action",
    recommendation_id: recommendation.id,
    recommendation_action: recommendationAction,
  }, {
    onSuccess: () => toast.success(
      recommendationAction === "mark_sent"
        ? "Marked sent; outcome tracking is now active"
        : "Recommendation updated",
    ),
  });
  const copyDraft = async () => {
    try {
      await navigator.clipboard.writeText(draftText);
      toast.success("Draft copied");
    } catch {
      toast.error("Could not copy the draft");
    }
  };

  const verifiedFacts = (stateQuery.data?.verified_facts ?? []).filter((fact: any) => fact.active);
  const governance = stateQuery.data?.governance ?? recommendationQuery.data?.rollout ?? null;
  const health = governance?.latest_health ?? null;
  const metricsByArm = useMemo(() => {
    const rows = stateQuery.data?.metrics ?? [];
    return (["standard", "personalized"] as const).map((arm) =>
      rows.filter((row: any) => row.experiment_arm === arm).reduce((totals: any, row: any) => ({
        arm,
        shown: totals.shown + Number(row.recommendations_shown ?? 0),
        accepted: totals.accepted + Number(row.accepted ?? 0),
        edited: totals.edited + Number(row.edited ?? 0),
        sent: totals.sent + Number(row.sent ?? 0),
        replied: totals.replied + Number(row.replied ?? 0),
        booked: totals.booked + Number(row.booked ?? 0),
        paid: totals.paid + Number(row.paid ?? 0),
        optedOut: totals.optedOut + Number(row.opted_out ?? 0),
        revenue: totals.revenue + Number(row.attributed_revenue ?? 0),
      }), {
        arm,
        shown: 0,
        accepted: 0,
        edited: 0,
        sent: 0,
        replied: 0,
        booked: 0,
        paid: 0,
        optedOut: 0,
        revenue: 0,
      })
    );
  }, [stateQuery.data?.metrics]);
  const lifecycle = (stateQuery.data?.recommendation_lifecycle ?? [])
    .filter((event: any) => !latestRecommendation || event.recommendation_id === latestRecommendation.id)
    .slice(0, 10);
  const persistedDraft = recommendation?.edited_text ?? recommendation?.draft_text ?? "";
  const hasUnsavedDraft = !!recommendation && draftText !== persistedDraft;

  const revokeFact = (factId: string) => actionMutation.mutate({
    action: "revoke_fact",
    fact_id: factId,
  }, { onSuccess: () => toast.success("Verified fact revoked; refresh queued") });

  const acknowledgePolicy = () => actionMutation.mutate({
    action: "acknowledge_policy",
  }, { onSuccess: () => toast.success("Governed-use policy acknowledged") });
  const updateRollout = (rolloutStage: string) => actionMutation.mutate({
    action: "update_rollout",
    rollout_stage: rolloutStage,
    reason: `owner_transition_to_${rolloutStage}`,
  }, { onSuccess: () => toast.success(`Rollout moved to ${rolloutStage}`) });
  const updatePilotPercentage = (pilotPercentage: number) => actionMutation.mutate({
    action: "update_rollout",
    pilot_percentage: pilotPercentage,
    reason: "owner_adjusted_pilot_percentage",
  }, { onSuccess: () => toast.success(`Pilot cohort set to ${pilotPercentage}%`) });
  const setPilot = (enabled: boolean) => actionMutation.mutate({
    action: "set_pilot",
    enabled,
  }, { onSuccess: () => toast.success(enabled ? "Customer added to pilot" : "Customer removed from pilot") });
  const setPaused = (paused: boolean) => {
    const reason = paused
      ? window.prompt("Why are recommendations being paused?", "Owner safety review")
      : "owner_resumed_after_review";
    if (paused && (!reason || !reason.trim())) return;
    actionMutation.mutate({
      action: "update_rollout",
      recommendations_paused: paused,
      pause_reason: reason,
    }, { onSuccess: () => toast.success(paused ? "Recommendations paused" : "Recommendations resumed") });
  };

  const rolloutStage = String(governance?.rollout_stage ?? "shadow");
  const nextStage: Record<string, string | null> = {
    off: "shadow",
    shadow: "pilot",
    pilot: "general",
    general: null,
  };
  const previousStage: Record<string, string | null> = {
    off: null,
    shadow: "off",
    pilot: "shadow",
    general: "pilot",
  };

  if (recommendationQuery.isLoading || stateQuery.isLoading) {
    return (
      <Card className="border-primary/20">
        <CardContent className="flex items-center gap-2 py-5 text-sm text-muted-foreground">
          <Sparkles className="h-4 w-4 animate-pulse" /> Preparing a safe next action…
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {governance && (
        <Card className="border-sky-500/25 bg-sky-500/5">
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Activity className="h-4 w-4 text-sky-700" /> Governance and rollout
                </CardTitle>
                <CardDescription className="mt-1">
                  Human review is mandatory. Automatic customer sending is structurally disabled.
                </CardDescription>
              </div>
              <div className="flex flex-wrap gap-1.5">
                <Badge variant="outline" className="capitalize">{rolloutStage}</Badge>
                {governance.recommendations_paused && <Badge variant="destructive">Paused</Badge>}
                {health?.status && (
                  <Badge variant="secondary" className="capitalize">Health: {health.status}</Badge>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3 text-xs">
            {!governance.policy_acknowledged_at && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-900">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <div>
                    <p className="font-medium">Owner acknowledgement required before pilot rollout</p>
                    <p className="mt-1 text-amber-800">
                      Sensitive traits remain prohibited, every recommendation requires human review, and Dibs will not send it automatically.
                    </p>
                  </div>
                </div>
                {governance.can_manage && (
                  <Button size="sm" variant="outline" className="mt-2 bg-white" onClick={acknowledgePolicy} disabled={actionMutation.isPending}>
                    <ShieldCheck className="h-4 w-4" /> Acknowledge governed use
                  </Button>
                )}
              </div>
            )}

            <div className="grid gap-2 sm:grid-cols-3">
              <div className="rounded-md border bg-background/80 p-2.5">
                <p className="font-medium">Current contact</p>
                <p className="mt-1 text-muted-foreground">
                  {governance.rollout_decision?.recommendationEnabled
                    ? "Recommendation access enabled"
                    : String(governance.rollout_decision?.reason ?? "not enabled").replace(/_/g, " ")}
                </p>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Stable cohort bucket {Number(governance.rollout_decision?.cohortBucket ?? 0) + 1}/100
                </p>
              </div>
              <div className="rounded-md border bg-background/80 p-2.5">
                <p className="font-medium">30-day quality</p>
                <p className="mt-1 text-muted-foreground">
                  {health ? `${Number(health.quality_failure_rate ?? 0).toLocaleString(undefined, { style: "percent", maximumFractionDigits: 1 })} negative · ${health.quality_sample} reviewed` : "Collecting pilot evidence"}
                </p>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Opt-outs {health ? Number(health.opt_out_rate ?? 0).toLocaleString(undefined, { style: "percent", maximumFractionDigits: 1 }) : "—"}
                </p>
              </div>
              <div className="rounded-md border bg-background/80 p-2.5">
                <p className="font-medium">Release gate</p>
                <p className="mt-1 text-muted-foreground">
                  {health?.ready_for_general ? "Ready for general rollout" : "Not yet ready for general rollout"}
                </p>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Minimum sample {governance.minimum_sample_size} per safety measure
                </p>
              </div>
            </div>

            {governance.pause_reason && (
              <p className="rounded-md border border-rose-200 bg-rose-50 p-2 text-rose-800">
                Pause reason: {governance.pause_reason}
              </p>
            )}

            {governance.can_manage ? (
              <div className="flex flex-wrap gap-2">
                {!governance.policy_acknowledged_at ? null : nextStage[rolloutStage] && (
                  <Button
                    size="sm"
                    onClick={() => updateRollout(nextStage[rolloutStage]!)}
                    disabled={actionMutation.isPending || (nextStage[rolloutStage] === "general" && !health?.ready_for_general)}
                  >
                    <Rocket className="h-4 w-4" /> Move to {nextStage[rolloutStage]}
                  </Button>
                )}
                {previousStage[rolloutStage] && (
                  <Button size="sm" variant="outline" onClick={() => updateRollout(previousStage[rolloutStage]!)} disabled={actionMutation.isPending}>
                    Roll back to {previousStage[rolloutStage]}
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setPilot(!governance.explicit_pilot)} disabled={actionMutation.isPending}>
                  {governance.explicit_pilot ? "Remove contact from pilot" : "Add contact to pilot"}
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="outline" disabled={actionMutation.isPending}>
                      Pilot {governance.pilot_percentage}%
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {[5, 10, 25, 50, 100].map((percentage) => (
                      <DropdownMenuItem key={percentage} onClick={() => updatePilotPercentage(percentage)}>
                        {percentage}% stable cohort
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button size="sm" variant={governance.recommendations_paused ? "default" : "destructive"} onClick={() => setPaused(!governance.recommendations_paused)} disabled={actionMutation.isPending}>
                  {governance.recommendations_paused ? <PlayCircle className="h-4 w-4" /> : <PauseCircle className="h-4 w-4" />}
                  {governance.recommendations_paused ? "Resume" : "Pause"}
                </Button>
              </div>
            ) : (
              <p className="text-muted-foreground">Rollout policy is owner-managed. Sales staff can review claims and use enabled recommendations.</p>
            )}

            {health?.readiness?.length > 0 && (
              <details className="rounded-md border bg-background/70 p-2.5">
                <summary className="cursor-pointer font-medium">General-release readiness</summary>
                <div className="mt-2 space-y-1.5">
                  {health.readiness.map((item: any) => (
                    <p key={item.key} className="flex items-start gap-1.5">
                      {item.passed ? <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 text-emerald-600" /> : <Clock className="mt-0.5 h-3.5 w-3.5 text-muted-foreground" />}
                      <span>{item.detail}</span>
                    </p>
                  ))}
                </div>
              </details>
            )}
          </CardContent>
        </Card>
      )}

      {verifiedFacts.length > 0 && (
        <Card className="border-emerald-500/25 bg-emerald-500/5">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm">
              <ShieldCheck className="h-4 w-4 text-emerald-700" /> Verified customer facts
            </CardTitle>
            <CardDescription>Salesperson-confirmed facts outrank AI inference.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {verifiedFacts.map((fact: any) => (
              <div key={fact.id} className="flex items-start justify-between gap-3 rounded-md border bg-background/80 p-2.5 text-xs">
                <div>
                  <p className="font-medium capitalize">{String(fact.category).replace(/_/g, " ")}</p>
                  <p className="mt-0.5 text-muted-foreground">{fact.value}</p>
                </div>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => revokeFact(fact.id)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card className="border-violet-500/25 bg-violet-500/5">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <Sparkles className="h-4 w-4 text-violet-600" /> Recommended next action
              </CardTitle>
              <CardDescription className="mt-1">
                Eligibility is enforced by code. Dibs creates drafts only and never sends automatically.
              </CardDescription>
            </div>
            {recommendation && (
              <Badge variant="outline" className="capitalize">
                {recommendation.status.replace(/_/g, " ")}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!recommendation ? (
            <div className="rounded-md border bg-background/70 p-3 text-sm">
              <p className="font-medium">No outreach recommendation right now</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {eligibility?.detail ?? "The customer does not currently pass the deterministic outreach checks."}
              </p>
              {eligibility?.retry_at && (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Recheck after {new Date(eligibility.retry_at).toLocaleString()}.
                </p>
              )}
            </div>
          ) : (
            <>
              <div className="grid gap-2 sm:grid-cols-2">
                <div className="rounded-md border bg-background/80 p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Action</p>
                  <p className="mt-1 text-sm font-medium">{recommendation.title}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{recommendation.rationale}</p>
                </div>
                <div className="rounded-md border bg-background/80 p-3 text-xs">
                  <p><span className="text-muted-foreground">Objective:</span> <span className="capitalize">{recommendation.objective}</span></p>
                  <p className="mt-1"><span className="text-muted-foreground">Channel:</span> <span className="capitalize">{recommendation.channel}</span></p>
                  <p className="mt-1"><span className="text-muted-foreground">Timing:</span> {new Date(recommendation.recommended_for).toLocaleString()}</p>
                  {recommendation.listing_name && <p className="mt-1"><span className="text-muted-foreground">Service:</span> {recommendation.listing_name}</p>}
                  {recommendation.offer_label && <p className="mt-1"><span className="text-muted-foreground">Offer:</span> {recommendation.offer_label}</p>}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 text-[10px]">
                <Badge variant="outline"><ShieldCheck className="mr-1 h-3 w-3" /> Eligible · draft only</Badge>
                <Badge variant="outline" className="capitalize">{recommendation.confidence} confidence</Badge>
                <Badge variant="outline">Experiment: {recommendation.experiment_arm}</Badge>
              </div>

              {!recommendation.draft_text && !recommendation.edited_text ? (
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={createDraft} disabled={actionMutation.isPending}>
                    <MessageSquare className="h-4 w-4" /> Create editable draft
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => recordAction("dismiss")} disabled={actionMutation.isPending}>
                    Dismiss
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => recordAction("not_relevant")} disabled={actionMutation.isPending}>
                    <ThumbsDown className="h-4 w-4" /> Not relevant
                  </Button>
                </div>
              ) : (
                <div className="space-y-2 rounded-md border bg-background/80 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-medium">Editable draft</p>
                    <Badge variant="secondary" className="text-[10px] capitalize">
                      {recommendation.draft_generation ?? "safe"} draft
                    </Badge>
                  </div>
                  <Textarea
                    value={draftText}
                    onChange={(event) => setDraftText(event.target.value)}
                    rows={6}
                    maxLength={5_000}
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={saveDraft} disabled={!draftText.trim() || !hasUnsavedDraft || actionMutation.isPending}>
                      <Check className="h-4 w-4" /> Save edits
                    </Button>
                    <Button size="sm" variant="outline" onClick={copyDraft} disabled={!draftText.trim()}>
                      <Clipboard className="h-4 w-4" /> Copy
                    </Button>
                    {recommendation.status !== "accepted" && recommendation.status !== "sent" && (
                      <Button size="sm" variant="outline" onClick={() => recordAction("accept")} disabled={actionMutation.isPending}>
                        <CheckCircle2 className="h-4 w-4" /> Accept
                      </Button>
                    )}
                    {recommendation.status !== "sent" && (
                      <Button size="sm" onClick={() => recordAction("mark_sent")} disabled={!draftText.trim() || hasUnsavedDraft || actionMutation.isPending}>
                        <Send className="h-4 w-4" /> Mark manually sent
                      </Button>
                    )}
                    {recommendation.status === "sent" && (
                      <Button size="sm" variant="outline" onClick={() => recordAction("complete")} disabled={actionMutation.isPending}>
                        Mark completed
                      </Button>
                    )}
                  </div>
                  {hasUnsavedDraft && (
                    <p className="text-[11px] text-amber-700">Save edits before marking this draft as manually sent.</p>
                  )}
                </div>
              )}

              {recommendation.evidence?.length > 0 && (
                <details className="rounded-md border bg-background/70 p-2.5 text-xs">
                  <summary className="cursor-pointer font-medium">
                    Evidence behind this recommendation ({recommendation.evidence.length})
                  </summary>
                  <ul className="mt-2 space-y-1.5 text-muted-foreground">
                    {recommendation.evidence.map((item: any, index: number) => (
                      <li key={`${item.source_ref}-${index}`}>
                        <span className="text-foreground">{item.claim}</span>
                        <span className="ml-1">· {item.source_ref} · {item.confidence}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              {recommendation.eligibility_snapshot && (
                <details className="rounded-md border bg-background/70 p-2.5 text-xs">
                  <summary className="cursor-pointer font-medium">Eligibility checks passed</summary>
                  <div className="mt-2 grid gap-1 text-muted-foreground sm:grid-cols-2">
                    {Object.entries(recommendation.eligibility_snapshot).map(([key, value]) => (
                      <p key={key}>
                        <span className="capitalize">{key.replace(/_/g, " ")}:</span>{" "}
                        <span className="text-foreground">{String(value)}</span>
                      </p>
                    ))}
                  </div>
                </details>
              )}

              {recommendation.do_not_mention?.length > 0 && (
                <details className="rounded-md border border-rose-200 bg-rose-50 p-2.5 text-xs text-rose-800">
                  <summary className="cursor-pointer font-medium">Internal guidance — never mention directly</summary>
                  <p className="mt-1">{recommendation.do_not_mention.join("; ")}</p>
                </details>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {lifecycle.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Recommendation journey</CardTitle>
            <CardDescription>Auditable salesperson decisions and attributed customer outcomes.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-1.5">
            {lifecycle.map((event: any) => (
              <div key={event.id} className="flex items-center justify-between gap-3 text-xs">
                <Badge variant="outline" className="capitalize">{String(event.event_type).replace(/_/g, " ")}</Badge>
                <span className="text-muted-foreground">{new Date(event.occurred_at).toLocaleString()}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {metricsByArm.some((metric) => metric.shown > 0) && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Sales learning</CardTitle>
            <CardDescription>Controlled standard holdout versus governed personalization.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 text-xs sm:grid-cols-2">
            {metricsByArm.map((metric) => (
              <div key={metric.arm} className="rounded-md border p-3">
                <p className="font-semibold capitalize">{metric.arm}</p>
                <div className="mt-2 grid grid-cols-3 gap-x-2 gap-y-1 text-muted-foreground">
                  <p>Shown <span className="text-foreground">{metric.shown}</span></p>
                  <p>Accepted <span className="text-foreground">{metric.accepted}</span></p>
                  <p>Edited <span className="text-foreground">{metric.edited}</span></p>
                  <p>Sent <span className="text-foreground">{metric.sent}</span></p>
                  <p>Replied <span className="text-foreground">{metric.replied}</span></p>
                  <p>Booked <span className="text-foreground">{metric.booked}</span></p>
                  <p>Paid <span className="text-foreground">{metric.paid}</span></p>
                  <p>Opt-outs <span className="text-foreground">{metric.optedOut}</span></p>
                  <p>Revenue <span className="text-foreground">${metric.revenue.toFixed(2)}</span></p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
